
  import { defineComponent, PropType } from 'vue';

  import { TBaseFilterPayload } from '@/common/types/filter';
  import { getRoutePathWithQuery } from '@/common/utils/router';
  import { getPreviousYear, getPreviousYearByYear } from '@/common/utils/year';

  import Loader from '@/components/Loader.vue';

  import {
    ALL_YEAR_OPTION,
    DASHBOARDS_INDICATOR_LINKS,
    DASHBOARDS_INDICATOR_TOOLTIPS,
  } from '../../../constants/dashboards';
  import { TBaseRegionFilter } from '../../../types/common';
  import { TDashboardMainFilter, TDashboardsIndicatorStatistic } from '../../../types/dashboards';
  import DashboardsCard from '../dashboards-card/DashboardsCard.vue';
  import type { TIndicatorsColor, TVariant } from '../dashboards-card/types';
  import DashboardsCardsWrapper from '../DashboardsCardsWrapper.vue';
  import { TDashboardsIndicatorCardsConfig } from './types';

  type TFilter = Partial<TDashboardMainFilter & TBaseRegionFilter & { countryId: number }>;
  type TFilterPayload = TBaseFilterPayload<TFilter>;
  // eslint-disable-next-line no-unused-vars
  type TGetIndicatorCardsLinkParams = (_index: number) => Record<string, string | number | null | undefined>;
  type TDashboardsIndicatorPreparedStatistic = TDashboardsIndicatorStatistic & { originalIndex: number; link: string };

  export default defineComponent({
    name: 'DashboardsIndicatorCards',
    components: {
      DashboardsCard,
      DashboardsCardsWrapper,
      Loader,
    },
    emits: ['card-click'],
    props: {
      filter: {
        type: Object as PropType<TFilter>,
        required: true,
      },
      config: {
        type: Object as PropType<TDashboardsIndicatorCardsConfig>,
        required: true,
      },
      clickableIndexes: {
        type: Array as PropType<Array<number>>,
        default: () => [],
      },
      hideEmptyCards: {
        type: Boolean,
        default: false,
      },
      hideAllCardsWhenEmpty: {
        type: Boolean,
        default: false,
      },
      variant: {
        type: String as PropType<TVariant>,
        default: 'link',
      },
      indicatorsColor: {
        type: String as PropType<TIndicatorsColor>,
      },
      getLinkParams: {
        type: Function as PropType<TGetIndicatorCardsLinkParams>,
      },
      isCardsWithGrid: {
        type: Boolean,
        default: true,
      },
      isShowTooltip: {
        type: Boolean,
        default: true,
      },
    },
    data(): {
      year: string;
      indicatorCardsAbortController: AbortController;
      isLoadingCards: boolean;
      isShowAPPG: boolean;
    } {
      return {
        year: '',
        indicatorCardsAbortController: new AbortController(),
        isLoadingCards: false,
        isShowAPPG: true,
      };
    },
    computed: {
      getIndicatorCardsData() {
        const getterData = this.$store.getters[this.config.getter] || [];

        const arr = Array.isArray(getterData.items) ? getterData.items : getterData;
        return JSON.parse(JSON.stringify(arr));
      },
      getRegionStatisticWithLinks() {
        const withLinks: TDashboardsIndicatorPreparedStatistic[] = this.getIndicatorCardsData.map(
          (item: TDashboardsIndicatorStatistic, index: number) => {
            return {
              ...item,
              link: getRoutePathWithQuery(this.$router, {
                name: DASHBOARDS_INDICATOR_LINKS[index],
                query: this.getLinkParams?.(index) ?? {},
              }),
              originalIndex: index,
            };
          },
        );

        if (this.hideEmptyCards) {
          return withLinks.filter((item) => item.amount > 0);
        }

        if (this.hideAllCardsWhenEmpty && withLinks.every((item) => item.amount <= 0)) return [];

        return withLinks;
      },
    },
    methods: {
      getIndicatorCards(payload: TFilterPayload) {
        return this.$store.dispatch(this.config.action, payload);
      },
      getYear() {
        return this.filter.year ? String(getPreviousYearByYear(this.filter.year)) : String(getPreviousYear());
      },
      async updateCards() {
        this.indicatorCardsAbortController.abort();
        this.indicatorCardsAbortController = new AbortController();

        const payload: TFilterPayload = {
          params: {
            ...this.filter,
            ...(this.filter.countryId ? { countryId: this.filter.countryId } : {}),
            ...(this.filter.year ? { year: this.filter.year } : {}),
          },
          abortController: this.indicatorCardsAbortController,
        };
        this.isLoadingCards = true;
        await this.getIndicatorCards(payload);
        this.year = this.getYear();
        this.isLoadingCards = false;
        this.isShowAPPG = this.filter.year !== ALL_YEAR_OPTION.value;
      },
      clickByCard(cardIndex: number) {
        if (!this.getIsClickable(cardIndex)) return;
        this.$emit('card-click', cardIndex);
      },
      getIsClickable(cardIndex: number) {
        return this.clickableIndexes.includes(cardIndex);
      },
      getTooltipText(cardIndex: number) {
        return DASHBOARDS_INDICATOR_TOOLTIPS[cardIndex] ?? '';
      },
    },
    watch: {
      filter: {
        handler() {
          this.updateCards();
        },
        immediate: true,
        deep: true,
      },
    },
  });
